import { useState, useEffect } from "react";
import api from "../../lib/api.ts";
import fallbackImage from "../../assets/bg-dashboard.png";
import { useLanguage } from "../../context/LanguageContext";
import placeholder from "../../assets/Car place holder PNG.png";
const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [error, setError] = useState(null);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage(); // get app language
  // Auto-scroll interval
  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollInterval = 5000; // 5 seconds

  // -----------------------Fetch Data----------------------
  useEffect(() => {
    const fetchData = async (lang) => {
      try {
        // const lang = navigator.language.startsWith("ar") ? "ar" : "en";
        const response = await api.get(
          `/pagesettings/homepage?lang=${language}` // Use the language from the browser
        );

        const rawHero =
          response?.data?.hero_section ?? response?.data?.data?.hero_section;

        // Normalize hero section into an array (API might return object, array, or null)
        const heroData = Array.isArray(rawHero)
          ? rawHero
          : rawHero && typeof rawHero === "object"
          ? Object.values(rawHero)
          : [];

        if (!heroData.length) {
          console.warn(
            "hero_section is empty or invalid, using fallback slide"
          );
        }

        const apiSlides = (
          heroData.length
            ? heroData
            : [
                {
                  title: { value: "Welcome to Our Site" },
                  subtitle: { value: "Discover amazing features" },
                  btnText: { value: "Explore Now" },
                  btnLink: { value: "#" },
                  imageUrl: { value: null },
                },
              ]
        ).map((slide, idx) => ({
          title: slide?.title?.value || "",
          subtitle: slide?.subtitle?.value || "",
          cta: slide?.btnText?.value || "", // Button text
          ctaUrl: slide?.btnLink?.value || "#", // Button URL
          bgimg: getImageUrl(slide?.imageUrl?.value, idx),
        }));

        setSlides(apiSlides);
      } catch (err) {
        console.error("❌ Error fetching slider data:", err);
        setError(err.message || "Failed to fetch slides");
        // Set fallback slide
        setSlides([
          {
            title: "Welcome to Our Site",
            subtitle: "Discover amazing features",
            cta: "Explore Now",
            ctaUrl: "#",
            // bgimg: placeholder,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  // Helper function for image URL
  const getImageUrl = (imageUrl) => {
    // Accept absolute URLs; otherwise, try to make absolute using API base or fall back
    if (typeof imageUrl === "string" && imageUrl.startsWith("http"))
      return imageUrl;
    if (typeof imageUrl === "string" && imageUrl.length > 0) {
      const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
      const path = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
      return base ? `${base}${path}` : placeholder || fallbackImage;
    }
    return placeholder || fallbackImage;
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScroll || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [autoScroll, slides.length]);

  // Reset to first slide when language or slides change
  useEffect(() => {
    setCurrentSlide(0);
  }, [language, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset auto-scroll timer when user interacts
    setAutoScroll(false);
    setTimeout(() => setAutoScroll(true), autoScrollInterval * 2);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (slides.length === 0) return <div>No slides available</div>;

  const isRTL = language === "ar";
  const translateValue = isRTL
    ? `${currentSlide * 100}%`
    : `-${currentSlide * 100}%`;

  return (
    <section className='relative text-white'>
      {/* Slider with background image and black overlay */}
      <div className='relative overflow-hidden'>
        <div
          className='flex transition-transform max-w-full md:h-[600px] duration-500 ease-in-out'
          style={{ transform: `translateX(${translateValue})` }}
          dir={isRTL ? "rtl" : "ltr"}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className='w-full bg-contain flex-shrink-0 py-20 relative'
              style={{
                backgroundImage: `url(${slide.bgimg})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
            >
              {/* Black overlay */}
              <div className='absolute inset-0'></div>

              {/* Slide Content */}
              <div className='relative w-full md:w-[951px] mx-auto px-4 sm:px-6 lg:px-8 text-center'>
                <h1 className='font-Poppins font-bold mb-3 text-2xl md:text-[64px] leading-[100%] text-white'>
                  {slide.title.includes("AUCTION") ? (
                    <>
                      {slide.title.split("AUCTION")[0]}
                      <span className='text-primary-dark'>AUCTION</span>
                      {slide.title.split("AUCTION")[1]}
                    </>
                  ) : (
                    slide.title
                  )}
                </h1>
                <p className='text-[32px] text-center mb-8 font-Poppins font-[400] text-gray-300'>
                  <span
                    className=''
                    dangerouslySetInnerHTML={{
                      __html: slide?.subtitle,
                    }}
                  />
                  {/* {slide.subtitle} */}
                </p>
                {slide.cta && (
                  <a
                    href={slide.ctaUrl}
                    className='inline-flex items-center justify-center bg-primary-dark w-[150px] md:w-[227px] h-[50px] md:h-[73px] text-xl md:text-[24px] font-Poppins font-medium text-white'
                    style={{ borderRadius: "10px" }}
                  >
                    {slide.cta}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        {slides.length > 1 && (
          <div
            className={`absolute bottom-7 md:bottom-14 ${
              isRTL ? "right-5 md:right-10" : "left-5 md:left-10"
            } flex items-center`}
          >
            <div
              className={`flex ${
                isRTL ? "space-x-reverse space-x-2" : "space-x-2"
              }`}
            >
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide
                      ? "bg-primary-dark w-6"
                      : "bg-white bg-opacity-50"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSlider;
